import { useCallback, useEffect, useRef, useState } from "react";
import type { PayrollStatementNotificationRecipient } from "./types";
import {
  getSelectedEligibleRecipients,
  reconcileSelectedRecipientIds,
} from "./utils";

export const useRecipientSelection = ({
  recipients,
  selectionKey,
  enabled,
}: {
  recipients: PayrollStatementNotificationRecipient[];
  selectionKey: string;
  enabled: boolean;
}) => {
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<Set<string>>(
    () => new Set(),
  );
  const initializedSelectionKeyRef = useRef<string | null>(null);
  const eligibleRecipients = recipients.filter(
    (recipient) => recipient.eligible,
  );
  const selectedEligibleRecipients = getSelectedEligibleRecipients(
    recipients,
    selectedRecipientIds,
  );

  useEffect(() => {
    if (!enabled) return;

    const useDefaultSelection =
      initializedSelectionKeyRef.current !== selectionKey;
    setSelectedRecipientIds((current) =>
      reconcileSelectedRecipientIds({
        recipients,
        selectedRecipientIds: current,
        useDefaultSelection,
      }),
    );
    initializedSelectionKeyRef.current = selectionKey;
  }, [enabled, recipients, selectionKey]);

  const toggleRecipient = useCallback(
    (recipient: PayrollStatementNotificationRecipient, checked: boolean) => {
      if (!enabled || !recipient.eligible) return;

      setSelectedRecipientIds((current) => {
        const next = new Set(current);
        if (checked) {
          next.add(recipient.technician_id);
        } else {
          next.delete(recipient.technician_id);
        }
        return next;
      });
    },
    [enabled],
  );

  const toggleAllEligible = useCallback(
    (checked: boolean) => {
      if (!enabled) return;

      setSelectedRecipientIds((current) => {
        const next = new Set(current);
        recipients.forEach((recipient) => {
          if (!recipient.eligible) return;
          if (checked) {
            next.add(recipient.technician_id);
          } else {
            next.delete(recipient.technician_id);
          }
        });
        return next;
      });
    },
    [enabled, recipients],
  );

  /**
   * Replaces the selection with exactly these technicians.
   *
   * Replaces rather than adds, which is the whole point of it: "resend to the 3 whose
   * pay changed" has to mean those 3 and not those 3 plus whoever the page had
   * selected by default — and the default selection is everybody eligible.
   */
  const selectOnly = useCallback(
    (chosen: PayrollStatementNotificationRecipient[]) => {
      if (!enabled) return;

      setSelectedRecipientIds(
        new Set(
          chosen
            .filter((recipient) => recipient.eligible)
            .map((recipient) => recipient.technician_id),
        ),
      );
    },
    [enabled],
  );

  return {
    selectedRecipientIds,
    eligibleRecipients,
    selectedEligibleRecipients,
    allEligibleSelected:
      eligibleRecipients.length > 0 &&
      selectedEligibleRecipients.length === eligibleRecipients.length,
    someEligibleSelected: selectedEligibleRecipients.length > 0,
    toggleRecipient,
    toggleAllEligible,
    selectOnly,
  };
};
